import { Localized } from "@fluent/react/compat";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-final-form";
import { graphql, QueryRenderer } from "react-relay";

import { useCoralContext } from "coral-framework/lib/bootstrap";
import {
  AddSubmitHook,
  SubmitHook,
  withSubmitHookContext,
} from "coral-framework/lib/form";
import { fetchQuery, useMutation } from "coral-framework/lib/relay";
import { HorizontalGutter, Spinner } from "coral-ui/components/v2";

import { BadgesConfigContainerQuery as QueryTypes } from "coral-admin/__generated__/BadgesConfigContainerQuery.graphql";

import ConfigBox from "../../ConfigBox";
import Header from "../../Header";
import BadgesByDateSection from "./BadgesByDateSection";
import UpdateSiteBadgeRulesMutation from "./UpdateSiteBadgeRulesMutation";

interface Props {
  disabled: boolean;
  addSubmitHook: AddSubmitHook;
  sitesWithBadges?: Set<string>;
  onBadgesSaved?: () => void;
}

const BadgesConfigContainerInner: React.FunctionComponent<Props> = ({
  disabled,
  addSubmitHook,
  sitesWithBadges = new Set(),
  onBadgesSaved,
}) => {
  const updateSiteBadges = useMutation(UpdateSiteBadgeRulesMutation);

  const submitHook: SubmitHook = useCallback(
    async (data, { onExecute }) => {
      const badgesByDateList = data.badgesByDate || [];
      const badgesBySite: Record<string, any[]> = {};

      badgesByDateList.forEach((badge: any) => {
        if (badge && badge.siteID && badge.badgeName && badge.endDate) {
          if (!badgesBySite[badge.siteID]) {
            badgesBySite[badge.siteID] = [];
          }
          badgesBySite[badge.siteID].push({
            badge: badge.badgeName,
            startDate: badge.startDate || null,
            endDate: badge.endDate,
            isUserSpecific: false,
          });
        }
      });

      const mutations: Array<() => Promise<any>> = [];

      // Update badges for each site that has badges in the form
      for (const siteID of Object.keys(badgesBySite)) {
        const badges = badgesBySite[siteID];
        if (badges.length > 0) {
          mutations.push(() => updateSiteBadges({ siteID, badges }));
        }
      }

      // Delete badges for sites that no longer have any
      for (const siteID of sitesWithBadges) {
        if (!badgesBySite[siteID]) {
          mutations.push(() => updateSiteBadges({ siteID, badges: [] }));
        }
      }

      // Execute all mutations sequentially to avoid race conditions
      if (mutations.length > 0) {
        onExecute(async () => {
          try {
            for (const mutation of mutations) {
              const result = await mutation();
              if (result && result.modifiedCount === 0) {
                // eslint-disable-next-line no-console
                console.warn(
                  "[BadgesConfig] No siteUsernames found for this site. " +
                    "Badges will only apply to users who have commented on this site."
                );
              }
            }
            if (onBadgesSaved) {
              onBadgesSaved();
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[BadgesConfig] Error updating badges:", error);
            throw error;
          }
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { badgesByDate, ...dataWithoutBadges } = data;
      return dataWithoutBadges;
    },
    [updateSiteBadges, sitesWithBadges, onBadgesSaved]
  );

  React.useEffect(() => {
    return addSubmitHook(submitHook);
  }, [addSubmitHook, submitHook]);

  return (
    <HorizontalGutter spacing={4}>
      <ConfigBox
        title={
          <Localized id="configure-badge-title">
            <Header container="h2">Badge Configuration</Header>
          </Localized>
        }
      >
        <BadgesByDateSection disabled={disabled} />
      </ConfigBox>
    </HorizontalGutter>
  );
};

const BadgesConfigContainer: React.FunctionComponent<Props> = ({
  disabled,
  addSubmitHook,
}) => {
  const { relayEnvironment } = useCoralContext();

  return (
    <QueryRenderer<QueryTypes>
      environment={relayEnvironment}
      query={graphql`
        query BadgesConfigContainerQuery {
          sites {
            edges {
              node {
                id
                name
              }
            }
          }
          settings {
            id
          }
        }
      `}
      variables={{}}
      render={({ error, props }) => {
        if (error) {
          return <div>Error loading sites</div>;
        }

        if (!props) {
          return <Spinner />;
        }

        return (
          <BadgesConfigContainerWithData
            disabled={disabled}
            addSubmitHook={addSubmitHook}
            sites={props.sites.edges.map((edge: any) => edge.node)}
          />
        );
      }}
    />
  );
};

interface BadgesConfigContainerWithDataProps extends Props {
  sites: Array<{ id: string; name: string }>;
}

const BadgesConfigContainerWithData: React.FunctionComponent<BadgesConfigContainerWithDataProps> = ({
  disabled,
  addSubmitHook,
  sites,
}) => {
  const { relayEnvironment } = useCoralContext();
  const form = useForm();
  const [initialized, setInitialized] = useState(false);
  const [badgesKey, setBadgesKey] = useState(0);
  const [sitesWithBadges, setSitesWithBadges] = useState<Set<string>>(
    new Set()
  );

  const loadBadges = useCallback(async () => {
    const badgesByDate: any[] = [];
    const sitesWithBadgesSet = new Set<string>();

    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) {
        return "";
      }
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      const badgeData: any = await fetchQuery(
        relayEnvironment,
        graphql`
          query BadgesConfigContainerLoadAllBadgesQuery {
            allSiteBadgeRules {
              siteID
              badges {
                badge
                startDate
                endDate
              }
            }
          }
        `,
        {},
        { force: true }
      );

      if (badgeData?.allSiteBadgeRules) {
        badgeData.allSiteBadgeRules.forEach((siteResult: any) => {
          if (siteResult.badges && siteResult.badges.length > 0) {
            sitesWithBadgesSet.add(siteResult.siteID);

            siteResult.badges.forEach((badge: any) => {
              badgesByDate.push({
                siteID: siteResult.siteID,
                badgeName: badge.badge,
                startDate: formatDate(badge.startDate),
                endDate: formatDate(badge.endDate),
              });
            });
          }
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BadgesConfig] Error loading badges:", err);
    }

    setSitesWithBadges(sitesWithBadgesSet);

    const currentFormState = form.getState();
    const newFormValues = {
      ...currentFormState.values,
      badgesByDate,
    };

    form.initialize(newFormValues);
    setBadgesKey((prev) => prev + 1);

    setInitialized(true);
  }, [relayEnvironment, form]);

  useEffect(() => {
    if (!initialized && sites.length > 0) {
      void loadBadges();
    }
  }, [initialized, sites, loadBadges]);

  const handleBadgesSaved = useCallback(async () => {
    await loadBadges();
  }, [loadBadges]);

  return (
    <BadgesConfigContainerInner
      key={badgesKey}
      disabled={disabled}
      addSubmitHook={addSubmitHook}
      sitesWithBadges={sitesWithBadges}
      onBadgesSaved={handleBadgesSaved}
    />
  );
};

const enhanced = withSubmitHookContext((addSubmitHook) => ({ addSubmitHook }))(
  BadgesConfigContainer
);

export default enhanced;
